/* DISCLOSURE PANELS IN THE MAIN NAV

   Opens the panels that hang off items in the floating nav bar — Services over
   the seven service pages, Polling over the archive's sections. The markup is
   in layouts/base.njk; the styling is in css/modules/55-nav-services-panel.css,
   which is class-based throughout, so a new panel needs no CSS of its own.

   Every `.nav-item--panel` is picked up here. An item is wired if it holds a
   `.nav-panel-toggle` whose aria-controls names a real element, which is the
   same contract the markup already has to meet to be announced correctly. A
   third panel needs no edit to this file.

   Panels are closed in the HTML and opened only from here, so with JS off each
   item is exactly what it was before: a link to its hub.

   ORDER: main.js calls this before initNavigation. Both listen for Escape on
   document, and when a panel is open inside the mobile curtain the panel is the
   inner layer — it has to close first and alone. Listeners on the same node
   fire in registration order, so registering first is what lets
   stopImmediatePropagation() below keep the curtain open. The document-level
   handlers are registered once for all panels rather than once per panel, so
   adding a panel cannot change that ordering. */

export function initNavPanels() {
  const panels = [...document.querySelectorAll('.nav-item--panel')]
    .map((item) => {
      const toggle = item.querySelector('.nav-panel-toggle');
      const id = toggle && toggle.getAttribute('aria-controls');
      const panel = id && document.getElementById(id);
      return panel ? { item, toggle, panel } : null;
    })
    .filter(Boolean);

  if (!panels.length) return;

  const isWide = () => window.matchMedia('(min-width: 1200px)').matches;
  const isOpen = (p) => p.toggle.getAttribute('aria-expanded') === 'true';
  const anyOpen = () => panels.some(isOpen);

  let hoverTimer = null;
  const clearHover = () => {
    if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
  };

  const close = (p) => {
    clearHover();
    p.panel.classList.remove('open');
    p.toggle.setAttribute('aria-expanded', 'false');
  };

  const closeAll = () => panels.forEach(close);

  /* Two panels open at once would overlap in the bar and leave the reader
     choosing between two lists neither of which they asked for twice. */
  const open = (p) => {
    clearHover();
    panels.forEach((other) => { if (other !== p) close(other); });
    p.panel.classList.add('open');
    p.toggle.setAttribute('aria-expanded', 'true');
  };

  panels.forEach((p) => {
    p.toggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      isOpen(p) ? close(p) : open(p);
    });

    /* Hover is an addition for mice, not the way in — touch and keyboard both
       go through the button. The close is delayed so the diagonal from the
       button to the far corner of the panel does not drop the panel on the way.

       No `(hover: hover) and (pointer: fine)` gate. It would be the usual
       guard, but it is not needed here and it cannot be tested: the parent link
       is never preventDefault'd, so the tap that synthesises a mouseenter on a
       touch screen still follows the link to the hub — there is no tap-opens-
       instead-of-navigating trap to guard against. Gating on it would also drop
       hover on hybrid laptops that report a coarse pointer. */
    p.item.addEventListener('mouseenter', () => { if (isWide()) open(p); });
    p.item.addEventListener('mouseleave', () => {
      if (!isWide()) return;
      clearHover();
      hoverTimer = setTimeout(() => close(p), 220);
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !anyOpen()) return;
    e.stopImmediatePropagation();
    const open_ = panels.find(isOpen);
    closeAll();
    if (open_) open_.toggle.focus();
  });

  document.addEventListener('click', (e) => {
    panels.forEach((p) => { if (isOpen(p) && !p.item.contains(e.target)) close(p); });
  });

  /* Tabbing past the last link in a panel lands outside its item; the panel
     should not stay open behind the rest of the bar. */
  document.addEventListener('focusin', (e) => {
    panels.forEach((p) => { if (isOpen(p) && !p.item.contains(e.target)) close(p); });
  });

  /* The hamburger closing the curtain leaves a panel expanded underneath it,
     which is what greets the next person who opens the menu. */
  const navToggle = document.querySelector('.nav-toggle');
  if (navToggle) navToggle.addEventListener('click', closeAll);

  /* Absolute panel above 1024, inline panel below. Crossing the boundary while
     open would leave it positioned for the layout it just left. */
  let wide = isWide();
  window.addEventListener('resize', () => {
    const now = isWide();
    if (now === wide) return;
    wide = now;
    closeAll();
  });
}
